import { useState } from 'react';
import {
  CloseOutlined,
  ContactSupportOutlined,
  ExpandMore,
  FeedbackOutlined,
  LightbulbOutlined,
  MarkChatReadOutlined,
  QuestionAnswerOutlined,
  StickyNote2Outlined,
  SupportAgentOutlined,
  ViewModuleOutlined,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Drawer,
  Fab,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { getColor } from '../../assets/theme';
import { Link } from 'react-router-dom';
import useLayoutContext from '../../hooks/useLayoutContext';
import { guideTitleType } from '../../store/slices/guide.slice';
import { appMode } from '../../utils/url';
import { useCurrentModule } from '@hooks/useCurrentModule';

/**
 * Help component provides a floating help button and a drawer with various support options.
 * It includes module guides, technical support hotline, ticketing support, chat support, and more.
 * The component adapts its behavior based on the current app mode and module.
 */
const Help = () => {
  const { textColor, primary } = getColor();
  const [open, setOpen] = useState(false);
  const [showText, setShowText] = useState(false);

  const currentModule = useCurrentModule();

  const { handleOpenModuleGuide, openComingSoon } = useLayoutContext();

  /**
   * Determines the guide name based on the current module.
   * @returns {guideTitleType} The guide name or null if not found.
   */
  const getGuideName = (): guideTitleType => {
    switch (currentModule) {
      case 'FO':
        return 'frontOffice';
      case 'UM':
        return 'usersManagement';
      default:
        return null;
    }
  };

  return (
    <>
      {/* Floating Help Button */}
      <Fab
        variant='extended'
        size='medium'
        color='primary'
        sx={{
          position: 'absolute',
          bottom: '20px',
          right: 0,
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
          gap: '8px',
          textTransform: 'capitalize',
        }}
        onClick={() => setOpen(true)}
        onMouseEnter={() => setShowText(true)}
        onMouseLeave={() => setShowText(false)}
      >
        {showText && 'Help'}
        <ContactSupportOutlined />
      </Fab>

      {/* Help Drawer */}
      <Drawer anchor='right' open={open} onClose={() => setOpen(false)}>
        {/* Close Button */}
        <Box
          sx={{ display: 'flex', justifyContent: 'flex-end', height: '52px' }}
        >
          <IconButton
            disableRipple
            sx={{ border: 0, color: textColor, margin: '24px' }}
            onClick={() => setOpen(false)}
          >
            <CloseOutlined sx={{ width: '24px', height: '24px' }} />
          </IconButton>
        </Box>

        {/* Help Content */}
        <Box
          sx={{ padding: { xs: '0 26px 26px 26px', sm: '0 52px 52px 52px' } }}
        >
          <Typography
            variant='h6'
            sx={{
              fontSize: '20px',
              fontWeight: '600',
              lineHeight: '160%',
              letterSpacing: '0.15px',
              marginBottom: '32px',
            }}
          >
            Need Help?
          </Typography>
          <Box sx={{ width: { xs: '100%', md: '400px' } }}>
            {/* Module Guide */}
            <Accordion expanded={false}>
              <AccordionSummary
                onClick={() => {
                  if (appMode === 'production' || appMode === 'uat') {
                    return openComingSoon();
                  }
                  const guideName = getGuideName();
                  handleOpenModuleGuide(guideName);
                  setOpen(false);
                }}
              >
                <Stack direction='row' gap='24px'>
                  <ViewModuleOutlined />
                  <Typography>Module Guide</Typography>
                </Stack>
              </AccordionSummary>
            </Accordion>

            {/* FAQ Page */}
            <Accordion expanded={false}>
              <AccordionSummary>
                <Stack direction='row' gap='24px'>
                  <QuestionAnswerOutlined />
                  <Link
                    to='https://servoitsolutions.ph/support/kb/faq.php?cid=222'
                    target='_blank'
                    style={{ textDecoration: 'none' }}
                  >
                    <Typography sx={{ color: textColor }}>FAQ Page</Typography>
                  </Link>
                </Stack>
              </AccordionSummary>
            </Accordion>

            {/* Technical Support Hotline */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Stack direction='row' gap='24px'>
                  <SupportAgentOutlined />
                  <Typography>Technical Support Hotline</Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Typography pb='12px'>
                  For immediate technical assistance, our 24/7 hotlines are just
                  a call away. Reach out to our dedicated team at:
                </Typography>

                <Link
                  to='callto:09176847000'
                  style={{
                    textDecoration: 'none',
                  }}
                >
                  <Typography sx={{ color: primary }}>
                    📞 0917 684 7000
                  </Typography>
                </Link>
                <Link
                  to='callto:09991777000'
                  style={{ textDecoration: 'none' }}
                >
                  <Typography sx={{ color: primary }}>
                    📞 0999 177 7000
                  </Typography>
                </Link>
              </AccordionDetails>
            </Accordion>

            {/* Ticketing Support */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Stack direction='row' gap='24px'>
                  <StickyNote2Outlined />
                  <Typography>Ticketing Support</Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Typography pb='12px'>
                  Dive into our comprehensive Support Site where you can explore
                  solutions, share ideas, and submit requests. We're ready to
                  turn your suggestions into reality!
                </Typography>
                <Link
                  to='https://servoitsolutions.ph/support'
                  target='_blank'
                  style={{ textDecoration: 'none' }}
                >
                  <Typography sx={{ color: primary }}>
                    Servo IT Solutions - Support
                  </Typography>
                </Link>
              </AccordionDetails>
            </Accordion>

            {/* Chat Support */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Stack direction='row' gap='24px'>
                  <MarkChatReadOutlined />
                  <Typography>Chat Support</Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Typography pb='12px'>
                  Prefer chatting? Our support agents are online and ready to
                  assist. Click the chat icon on our website, and let's get the
                  conversation started.
                </Typography>
                <Link
                  to='https://servoitsolutions.ph'
                  target='_blank'
                  style={{ textDecoration: 'none' }}
                >
                  <Typography sx={{ color: primary }}>
                    Servo IT Solutions - Chat Support
                  </Typography>
                </Link>
              </AccordionDetails>
            </Accordion>

            {/* EdApp */}
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMore />}
                onClick={() => {
                  if (appMode === 'production' || appMode === 'uat') {
                    return openComingSoon();
                  }
                }}
              >
                <Stack direction='row' gap='24px'>
                  <LightbulbOutlined />
                  <Typography>EdApp</Typography>
                </Stack>
              </AccordionSummary>
              {appMode !== 'production' && appMode !== 'uat' && (
                <AccordionDetails>
                  <Typography pb='12px'>
                    Elevate your skills and make the most of our products
                    through Servo Academy. Accessible on the web or by
                    downloading EdApp's iOS or Android applications.
                  </Typography>
                  <Link
                    to='https://web.edapp.com/#login'
                    target='_blank'
                    style={{ textDecoration: 'none' }}
                  >
                    <Typography sx={{ color: primary }}>
                      Servo Academy - Web
                    </Typography>
                  </Link>
                </AccordionDetails>
              )}
            </Accordion>

            {/* Feedback */}
            <Accordion expanded={false}>
              <AccordionSummary>
                <Stack direction='row' gap='24px'>
                  <FeedbackOutlined />
                  <Link
                    to='https://forms.office.com/r/wpzA26xZEa?embed=true'
                    target='_blank'
                    style={{ textDecoration: 'none' }}
                  >
                    <Typography sx={{ color: textColor }}>Feedback</Typography>
                  </Link>
                </Stack>
              </AccordionSummary>
            </Accordion>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default Help;
